"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * Collection of regexs for our whitelist of host origins.
 */
var allowedOrigins = [
    /^https\:\/\/[\w\-\.]+\.blackbaud\.com$/,
    /^https\:\/\/[\w\-\.]+\.blackbaud\-dev\.com$/,
    /^http\:\/\/[\w\-\.]+\.blackbaud\-dev\.com$/,
    /^https\:\/\/[\w\-\.]+\.blackbaudhosting\.com$/,
    /^https\:\/\/[\w\-\.]+\.bbcloudservices\.com$/,
    /^https\:\/\/localhost(\:[0-9]+)?$/,
    /^https\:\/\/secure[2|3|8]\.convio\.net$/,
    /^https\:\/\/testweb\.convio\.com$/,
    /^https\:\/\/[\w\-\.]+\.conviocloud\.com$/,
    /^https\:\/\/[\w\-\.]+\.blackbaudcloud\.com$/
];
/**
 * Client for interacting with the parent page hosting the add-in.
 */
var AddinClient = /** @class */ (function () {
    function AddinClient(args) {
        var _this = this;
        this.args = args;
        /**
         * Tracks pending request to reeceive an auth-token from the host.
         */
        this.authTokenRequests = [];
        /**
         * Counter to provide unique ids for each auth token request.
         */
        this.lastAuthTokenRequestId = 0;
        /**
         * Tracks modal add-ins that have been launched from this add-in.
         */
        this.modalRequests = [];
        /**
         * Counter to provide unique ids for each modal request.
         */
        this.lastModalRequestId = 0;
        /**
         * Stores the registered add-in events.
         * Key - the event type.
         * Value - The callback function to be executed when the event type occurs.
         */
        this.registeredAddinEvents = {};
        this.windowMessageHandler = function (event) {
            _this.handleMessage(event);
        };
        // Listen to messages from the host page.
        window.addEventListener('message', this.windowMessageHandler);
        // Inform the host page that the add-in is loaded and listening for messages.
        this.raiseAddinReadyMessage();
    }
    /* istanbul ignore next */
    /**
     * @returns {string}  Returns the current query string path for the window, prefixed with ?.
     */
    AddinClient.getQueryString = function () {
        return window.location.search;
    };
    /**
     * Cleans up the AddinClient, releasing all resources.
     */
    AddinClient.prototype.destroy = function () {
        window.removeEventListener('message', this.windowMessageHandler);
        if (this.heightChangeIntervalId) {
            clearInterval(this.heightChangeIntervalId);
        }
    };
    /**
     * Requests the host page to navigate.
     * @param args Arguments describing the navigation request.
     */
    AddinClient.prototype.navigate = function (args) {
        this.postMessageToHostPage({
            message: {
                url: args.url
            },
            messageType: 'navigate'
        });
    };
    /**
     * Requests an authentication token for the current user.
     * @deprecated Use getUserIdentityToken() instead.
     * @returns {Promise<any>} Returns a promise which will resolve with the token value.
     */
    AddinClient.prototype.getAuthToken = function () {
        return this.getUserIdentityToken();
    };
    /**
     * Requests a user identity token for the current user.
     * @returns {Promise<any>} Returns a promise which will resolve with the token value.
     */
    AddinClient.prototype.getUserIdentityToken = function () {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var authTokenRequestId = ++_this.lastAuthTokenRequestId;
            _this.authTokenRequests[authTokenRequestId] = {
                reject: reject,
                resolve: resolve
            };
            _this.postMessageToHostPage({
                message: {
                    authTokenRequestId: authTokenRequestId
                },
                messageType: 'get-auth-token'
            });
        });
    };
    /**
     * Requests the host page to launch a modal add-in.
     * @param args Arguments for launching the modal.
     * @returns {Promise<any>} Returns a promise that will be resolved when the modal add-in is closed.
     * Promise will resolve with context data passed by from the modal add-in's closeModal call.
     */
    AddinClient.prototype.showModal = function (args) {
        var _this = this;
        return {
            modalClosed: new Promise(function (resolve, reject) {
                var modalRequestId = ++_this.lastModalRequestId;
                _this.modalRequests[modalRequestId] = {
                    reject: reject,
                    resolve: resolve
                };
                _this.postMessageToHostPage({
                    message: {
                        args: args,
                        modalRequestId: modalRequestId
                    },
                    messageType: 'show-modal'
                });
            })
        };
    };
    /**
     * Informs the host to close this modal add-in.
     * Should only be used from within the modal add-in and not the parent add-in.
     * @param args Arguments to provide a context object back to the parent add-in.
     */
    AddinClient.prototype.closeModal = function (args) {
        this.postMessageToHostPage({
            message: args,
            messageType: 'close-modal'
        });
    };
    /**
     * Informs the host to open the help tab with the specified help key.
     * @param args Arguments for launching the help tab.
     */
    AddinClient.prototype.openHelp = function (args) {
        this.postMessageToHostPage({
            message: {
                helpKey: args.helpKey
            },
            messageType: 'open-help'
        });
    };
    /**
     * Informs the host to show a toast message.
     * @param args Arguments for showing a toast.
     */
    AddinClient.prototype.showToast = function (args) {
        this.postMessageToHostPage({
            message: args,
            messageType: 'show-toast'
        });
    };
    /**
     * Requests the host page to launch a flyout add-in.
     * @param args Arguments for launching the flyout.
     * @returns {Promise<any>} Returns a promise that will be resolved when the flyout add-in is closed.
     */
    AddinClient.prototype.showFlyout = function (args) {
        var _this = this;
        return {
            flyoutClosed: new Promise(function (resolve, reject) {
                // assign default values if not specified,
                // consistent with SKY UX flyout defaults
                args.defaultWidth = args.defaultWidth || 500;
                args.maxWidth = args.maxWidth || args.defaultWidth;
                args.minWidth = args.minWidth || 320;
                _this.flyoutRequest = {
                    reject: reject,
                    resolve: resolve
                };
                _this.postMessageToHostPage({
                    message: args,
                    messageType: 'show-flyout'
                });
            })
        };
    };
    /**
     * Requests the host page to close the flyout add-in.
     */
    AddinClient.prototype.closeFlyout = function () {
        this.postMessageToHostPage({
            messageType: 'close-flyout'
        });
    };
    /**
     * Requests the host page to show a confirm dialog.
     * @param args Arguments for showing a confirm dialog.
     * @returns {Promise<string>} Returns a promise that will resolve with the
     * confirm action when the dialog is closed.
     */
    AddinClient.prototype.showConfirm = function (args) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            _this.confirmRequest = {
                reject: reject,
                resolve: resolve
            };
            _this.postMessageToHostPage({
                message: args,
                messageType: 'show-confirm'
            });
        });
    };
    /**
     * Informs the host to show an error dialog.
     * @param args Arguments for showing an error dialog.
     */
    AddinClient.prototype.showError = function (args) {
        this.postMessageToHostPage({
            message: args,
            messageType: 'show-error'
        });
    };
    /**
     * Requests the host page to show the page blocking wait indicator.
     */
    AddinClient.prototype.showWait = function () {
        this.postMessageToHostPage({
            messageType: 'show-wait'
        });
    };
    /**
     * Requests the host page to hide the page blocking wait indicator.
     */
    AddinClient.prototype.hideWait = function () {
        this.postMessageToHostPage({
            messageType: 'hide-wait'
        });
    };
    /**
     * Regiesters a callback to be executed when the specified event type occurs.
     * @param eventType The event type to process.
     * @param callback The callback to executes when the event occurs.
     */
    AddinClient.prototype.addEventHandler = function (eventType, callback) {
        this.registeredAddinEvents[eventType] = callback;
    };
    /**
     * Post a message to the host page informing it that the add-in is
     * now started and listening for messages from the host.
     */
    AddinClient.prototype.raiseAddinReadyMessage = function () {
        // No sensitive data should be provided with this message!  This is the initial
        // message posted to the host page to establish whether the host origin is a
        // trusted origin and therefore will post to any host, trusted or untrusted.
        // The host should respond with a host-ready message which will include the origin
        // and will be validated against a whitelist of allowed origins so that subsequent
        // messages can be posted only to that trusted origin.
        this.postMessageToHostPage({
            messageType: 'ready'
        }, '*');
    };
    /**
     * Handles the modal-closed message from the host.
     * This is emitted to add-ins which have previously launched a modal, which is now
     * closing.
     * @param message The message data, which includes a context object from the closing modal, which should be passed
     * to the calling modal in the showModal promise.
     */
    AddinClient.prototype.handleModalClosedMessage = function (message) {
        var modalRequests = this.modalRequests;
        var modalRequestId = message.modalRequestId;
        var modalRequest = modalRequests[modalRequestId];
        modalRequest.resolve(message.context);
        modalRequests[modalRequestId] = undefined;
    };
    /**
     * Handles host message responses to a get-auth-token request.
     * @param data The message.
     */
    AddinClient.prototype.handleAuthTokenMessage = function (data) {
        var authTokenRequests = this.authTokenRequests;
        var authTokenRequestId = data.message.authTokenRequestId;
        var authTokenRequest = authTokenRequests[authTokenRequestId];
        /* tslint:disable-next-line switch-default */
        switch (data.messageType) {
            case 'auth-token':
                var authToken = data.message.authToken;
                authTokenRequest.resolve(authToken);
                break;
            case 'auth-token-fail':
                authTokenRequest.reject(data.message.reason);
                break;
        }
        authTokenRequests[authTokenRequestId] = undefined;
    };
    /**
     * Handles message events received from the add-in host page.
     * @param event The event posted from the host.
     */
    AddinClient.prototype.handleMessage = function (event) {
        var _this = this;
        var data = event.data;
        if (data && data.source === 'bb-addin-host') {
            if (data.messageType === 'host-ready') {
                // The 'host-ready' message is the only message that's not validated against
                // the host origin since that is what's being established in the message.
                // This MUST be the first message posted by the host page or all further
                // communications with the host page will be blocked.
                this.setKnownAllowedHostOrigin(event.origin);
                this.trackHeightChangesOfAddinContent();
                // Pass key data to the add-in for it to initiailze.
                this.args.callbacks.init({
                    context: data.message.context,
                    envId: data.message.envId,
                    ready: function (args) {
                        // Do an immediate height check since the add-in may render something
                        // due to the context provided.  No need to wait a full second to reflect.
                        _this.checkForHeightChangesOfAddinContent();
                        _this.postMessageToHostPage({
                            message: args,
                            messageType: 'addin-ready'
                        });
                    }
                });
            }
            else if (this.isFromValidOrigin(event)) {
                /* tslint:disable-next-line switch-default */
                switch (data.messageType) {
                    case 'auth-token':
                    case 'auth-token-fail':
                        this.handleAuthTokenMessage(data);
                        break;
                    case 'modal-closed':
                        this.handleModalClosedMessage(data.message);
                        break;
                    case 'button-click':
                        if (this.args.callbacks.buttonClick) {
                            this.args.callbacks.buttonClick();
                        }
                        break;
                    case 'update-context':
                        if (this.args.callbacks.updateContext) {
                            this.args.callbacks.updateContext(data.message);
                        }
                        break;
                    case 'confirm-closed':
                        if (this.confirmRequest) {
                            this.confirmRequest.resolve(data.message.reason);
                            this.confirmRequest = undefined;
                        }
                        break;
                    case 'flyout-closed':
                        if (this.flyoutRequest) {
                            this.flyoutRequest.resolve();
                            this.flyoutRequest = undefined;
                        }
                        break;
                    case 'flyout-next-click':
                        if (this.args.callbacks.flyoutNextClick) {
                            this.args.callbacks.flyoutNextClick();
                        }
                        break;
                    case 'flyout-previous-click':
                        if (this.args.callbacks.flyoutPreviousClick) {
                            this.args.callbacks.flyoutPreviousClick();
                        }
                        break;
                    case 'help-click':
                        if (this.args.callbacks.helpClick) {
                            this.args.callbacks.helpClick();
                        }
                        break;
                    case 'settings-click':
                        if (this.args.callbacks.settingsClick) {
                            this.args.callbacks.settingsClick();
                        }
                        break;
                    case 'host-event':
                        this.processHostEvent(data.message);
                        break;
                }
            }
            else {
                this.warnInvalidOrigin();
            }
        }
    };
    /**
     * Validates and registers a value as the origin of the parent page hosting the add-in.
     * If the provided origin matches against our trusted whitelist, then it will be saved.
     * Post messages will implicitly go to this origin and received messages will be filtered
     * to just messages from this origin.
     * @param hostOrigin
     */
    AddinClient.prototype.setKnownAllowedHostOrigin = function (hostOrigin) {
        for (var _i = 0, allowedOrigins_1 = allowedOrigins; _i < allowedOrigins_1.length; _i++) {
            var allowedOrigin = allowedOrigins_1[_i];
            if (allowedOrigin.test(hostOrigin)) {
                this.trustedOrigin = hostOrigin;
                return;
            }
        }
    };
    /**
     * Checks if the height of the iFrame has changed since it was last
     * posted to the host page (or if it hasn't been posted yet) and initiates
     * a new post if so.
     */
    AddinClient.prototype.checkForHeightChangesOfAddinContent = function () {
        // after some discussion and experimentation, using offsetHeight appears to be sufficient
        var newHeight = document.documentElement.offsetHeight;
        if (newHeight !== this.lastPostedIframeHeight) {
            this.lastPostedIframeHeight = newHeight;
            this.postMessageToHostPage({
                message: {
                    height: newHeight + 'px'
                },
                messageType: 'height-change'
            });
        }
    };
    /**
     * Starts a timeout interval to watch for height changes
     * of the iframe content.
     */
    AddinClient.prototype.trackHeightChangesOfAddinContent = function () {
        var _this = this;
        this.heightChangeIntervalId = setInterval(function () {
            _this.checkForHeightChangesOfAddinContent();
        }, 1000);
    };
    /**
     * Posts a message to the parent window.
     * @param message The message content to post.
     * @param targetOrigin Optional. If provided, then the message will be posted to this origin.
     *                     If not, then it will post to the pre-determined host page origin.
     */
    AddinClient.prototype.postMessageToHostPage = function (message, targetOrigin) {
        message.source = 'bb-addin-client';
        message.addinId = this.getQueryVariable('addinId');
        targetOrigin = targetOrigin || this.trustedOrigin;
        if (targetOrigin) {
            window.parent.postMessage(message, targetOrigin);
        }
        else {
            this.warnInvalidOrigin();
        }
    };
    /**
     * Processes an add-in event that occurs from the host and responds
     * back to the host with an 'event-received' message.
     * @param message The message to process by looking up executing the registered callback
     * that matches the event type.
     */
    AddinClient.prototype.processHostEvent = function (message) {
        var eventArgs = message.context;
        var callback = this.registeredAddinEvents[eventArgs.type];
        if (callback) {
            callback(eventArgs.context);
        }
        this.postMessageToHostPage({
            message: {
                eventRequestId: message.eventRequestId
            },
            messageType: 'event-received'
        });
    };
    /**
     * Checks whether a MessageEvent is from the execetd host origin.
     * @param event
     */
    AddinClient.prototype.isFromValidOrigin = function (event) {
        return event.origin === this.trustedOrigin;
    };
    /**
     * Log that a message was received with an invalid origin.
     */
    AddinClient.prototype.warnInvalidOrigin = function () {
        console.warn('The origin is not trusted because the host-ready message has not been ' +
            'sent or because the host origin is not a whitelisted origin.');
    };
    /**
     * Reads a query string value from the current window location.
     * @param variable  Name of the query string parameter to ready.
     * @returns         The value of the query string parameter.
     */
    AddinClient.prototype.getQueryVariable = function (variable) {
        var query = AddinClient.getQueryString().substring(1);
        var vars = query.split('&');
        for (var _i = 0, vars_1 = vars; _i < vars_1.length; _i++) {
            var v = vars_1[_i];
            var pair = v.split('=');
            if (decodeURIComponent(pair[0]) === variable) {
                return decodeURIComponent(pair[1]);
            }
        }
    };
    return AddinClient;
}());
exports.AddinClient = AddinClient;
//# sourceMappingURL=addin-client.js.map